import { Form } from 'react-bootstrap';
import { Field } from 'formik';
import { useTranslation } from 'react-i18next';
import type { LeadType, ObjectType, TaskCreateFormType, TaskForType, UserType } from '@econtractor/types';
import { UserSelected } from 'modules/user';
import Editor from 'components/editors/Editor';
import TaskDueDateFields from '../../shared/TaskDueDateFields';
import TaskFor from '../../shared/TaskFor';
import SolarIcon from 'components/icons/SolarIcon';

interface NewTaskGeneralTabProps {
    values: TaskCreateFormType;
    setFieldValue: (field: string, value: unknown) => void;
    isSubmitting: boolean;
    showDescription: boolean;
    setShowDescription: (show: boolean) => void;
    canAssignMyself: (current: UserType | null) => boolean;
    assignMyself: (field: 'executor' | 'controller') => void;
    handleEndDateChange: (isoDate: string) => void;
    handleStartDateChange: (isoDate: string) => void;
    handleWithTimeChange: (checked: boolean) => void;
    handleStartTimeChange: (time: string) => void;
    handleEndTimeChange: (time: string) => void;
    handleMultiDayChange: (checked: boolean) => void;
    /** Тип прив'язки зафіксовано (відкрито з заявки/об'єкта) */
    taskForLocked?: boolean;
}

const NewTaskGeneralTab = ({
    values,
    setFieldValue,
    isSubmitting,
    showDescription,
    setShowDescription,
    canAssignMyself,
    assignMyself,
    handleEndDateChange,
    handleStartDateChange,
    handleWithTimeChange,
    handleStartTimeChange,
    handleEndTimeChange,
    handleMultiDayChange,
    taskForLocked = false,
}: NewTaskGeneralTabProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    
    const addMyselfBtn = (field: 'executor' | 'controller', current: UserType | null) => {
        if (!canAssignMyself(current)) return null;
        return (
            <button
                type="button"
                className="btn btn-link p-0 align-baseline fs-12"
                disabled={isSubmitting}
                onClick={() => assignMyself(field)}
            >
                <SolarIcon name="person-plus" className="me-1" />
                {t('btnAddMyself')}
            </button>
        );
    };

    return (
        <>
            <TaskFor
                taskFor={values.taskFor}
                lead={values.lead}
                object={values.object}
                disabled={isSubmitting}
                locked={taskForLocked}
                onTaskForChange={(next: TaskForType) => setFieldValue('taskFor', next)}
                onLeadChange={(lead: LeadType | null) => setFieldValue('lead', lead)}
                onObjectChange={(object: ObjectType | null) => setFieldValue('object', object)} 
            />

            <Form.Group className="mb-3">
                <label className="form-label text-muted">{t('labelName')}</label>
                <Field
                    autoFocus={true}
                    name="taskName"
                    className="form-control"
                    placeholder={t('labelName')}
                />
            </Form.Group>

            <Form.Group className="mb-3">
                {showDescription ? (
                    <>
                        <label className="form-label text-muted">{t('labelInfo')}</label>
                        <Editor
                            value={values.taskInfo || ''}
                            onChange={(newValue: string) => setFieldValue('taskInfo', newValue)}
                        />
                    </>
                ) : (
                    <button
                        type="button"
                        className="btn btn-link p-0 align-baseline"
                        onClick={() => setShowDescription(true)}
                    >
                        <SolarIcon name="plus" className="me-1" />
                        {t('btnAddInfo')}
                    </button>
                )}
            </Form.Group>

            <UserSelected
                title={t('labelExecutor')}
                value={values.executor}
                onChange={(user) => setFieldValue('executor', user)}
                disabled={isSubmitting}
                maxHeight={200}
                rightElement={addMyselfBtn('executor', values.executor)}
            />

            <UserSelected
                title={t('labelController')}
                value={values.controller}
                onChange={(user) => setFieldValue('controller', user)}
                disabled={isSubmitting}
                maxHeight={200}
                rightElement={addMyselfBtn('controller', values.controller)}
            />

            <TaskDueDateFields
                multiDay={values.multiDay}
                withTime={values.withTime}
                dateStart={values.dateStart}
                dateEnd={values.dateEnd}
                startTime={values.startTime}
                endTime={values.endTime}
                onMultiDayChange={handleMultiDayChange}
                onWithTimeChange={handleWithTimeChange}
                onStartDateChange={handleStartDateChange}
                onEndDateChange={handleEndDateChange}
                onStartTimeChange={handleStartTimeChange}
                onEndTimeChange={handleEndTimeChange}
            />
        </>
    );
};

export default NewTaskGeneralTab;
